import { useEffect } from 'react';
import {
  SHOPPING_DIVIDER_SPRITE,
  SHOPPING_DIVIDER_SYMBOL_ID,
  SHOPPING_DIVIDER_USE_HTML,
} from './dividerSvg';
import styles from './shoppingDivider.module.scss';

const SUBBUZZ_WRAPPER = '.js-subbuzz-wrapper';
const AD_SELECTORS = ['.js-ad-placement', '.js-ssr-ad-placement'];
const DIVIDER_CLASS = 'js-shopping-divider';
const SPRITE_HOLDER_ID = 'js-shopping-divider-sprite';

const isVisible = (el) =>
  !!el && (el.offsetParent !== null || el.getClientRects().length > 0);

const isFilledAdPlacement = (el) => {
  if (!el || !isVisible(el)) return false;
  if (el.children?.length > 0) return true;
  return el.getBoundingClientRect().height > 1;
};

const hasVisibleAd = (el) => {
  if (!el) return false;
  return AD_SELECTORS.some((sel) => {
    if (el.matches?.(sel) && isFilledAdPlacement(el)) return true;
    const matches = el.querySelectorAll?.(sel) || [];
    return Array.from(matches).some(isFilledAdPlacement);
  });
};

const ensureSpriteInjected = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SHOPPING_DIVIDER_SYMBOL_ID)) return;
  const holder = document.createElement('div');
  holder.id = SPRITE_HOLDER_ID;
  holder.setAttribute('aria-hidden', 'true');
  holder.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
  holder.innerHTML = SHOPPING_DIVIDER_SPRITE;
  document.body.appendChild(holder);
};

const buildDivider = () => {
  const wrapper = document.createElement('div');
  wrapper.className = `${DIVIDER_CLASS} ${styles.dividerWrapper}`;
  wrapper.innerHTML = SHOPPING_DIVIDER_USE_HTML;
  wrapper.firstElementChild?.classList.add(styles.divider);
  return wrapper;
};

/**
 * Inserts a decorative divider between every pair of adjacent subbuzzes
 * that does not have an ad between them. Re-runs on DOM mutations so it
 * stays correct as ads load asynchronously.
 */
export default function useShoppingDividers(containerRef, enabled) {
  useEffect(() => {
    if (!enabled) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    ensureSpriteInjected();

    const sync = () => {
      const subbuzzes = Array.from(container.querySelectorAll(SUBBUZZ_WRAPPER));
      const keep = new Set();

      for (let i = 0; i < subbuzzes.length - 1; i++) {
        const current = subbuzzes[i];
        const next = subbuzzes[i + 1];

        let hasAdBetween = hasVisibleAd(current);
        let cursor = current.nextElementSibling;
        while (!hasAdBetween && cursor && cursor !== next) {
          if (hasVisibleAd(cursor)) hasAdBetween = true;
          cursor = cursor.nextElementSibling;
        }

        if (hasAdBetween) continue;

        const prev = next.previousElementSibling;
        if (prev?.classList.contains(DIVIDER_CLASS)) {
          keep.add(prev);
        } else {
          keep.add(next.parentNode.insertBefore(buildDivider(), next));
        }
      }

      // Remove any dividers not in the keep set.
      container.querySelectorAll(`.${DIVIDER_CLASS}`).forEach((el) => {
        if (!keep.has(el)) el.remove();
      });
    };

    sync();

    if (typeof MutationObserver === 'undefined') return undefined;

    const observerOptions = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    };

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        observer.disconnect();
        sync();
        observer.observe(container, observerOptions);
      });
    });
    observer.observe(container, observerOptions);

    return () => observer.disconnect();
  }, [containerRef, enabled]);
}
